import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { NAV, SITE } from "@/config/site";
import { cn } from "@/lib/utils";
import { contextForPath, track, whatsappLink } from "@/lib/site";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Container, QuoteButton, WhatsAppButton, WhatsAppIcon } from "./Primitives";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function DesktopDropdown({ item }) {
  const { pathname } = useLocation();
  const active = item.children.some((c) => c.to === pathname);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn("kl-nav-link inline-flex items-center gap-1 outline-none", active && "is-active")}
        data-testid={`nav-${slug(item.label)}-trigger`}
      >
        {item.label}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={10} className="w-[320px] rounded-[14px] border-black/10 bg-white p-2 shadow-[0_18px_50px_rgba(0,0,0,0.10)]">
        {item.children.map((c) => (
          <DropdownMenuItem key={c.to} asChild className="cursor-pointer rounded-[10px] px-3 py-2.5 focus:bg-[#F7F7F7]">
            <Link to={c.to} data-testid={`nav-dropdown-${slug(c.label)}-link`} className="flex flex-col items-start gap-0.5">
              <span className="text-sm font-semibold text-[#161616]">{c.label}</span>
              <span className="text-xs text-black/55">{c.desc}</span>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="hidden bg-[#161616] text-white md:block">
        <Container className="flex h-9 items-center justify-between text-[12px] tracking-wide text-white/75">
          <p><span className="font-semibold tracking-[0.22em] text-white">{SITE.motto}</span><span className="mx-3 text-white/30">|</span>{SITE.ownership} printing & branding · {SITE.locationLabel}</p>
          <a href={`tel:${SITE.phoneE164}`} onClick={() => track("phone_click", { label: "topbar" })} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="topbar-phone-link">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {SITE.phoneDisplay}
          </a>
        </Container>
      </div>
      <header className={cn("sticky top-0 z-50 border-b bg-white transition-shadow duration-200", scrolled ? "border-black/10 shadow-[0_6px_24px_rgba(0,0,0,0.05)]" : "border-black/[0.06]")} data-testid="site-header">
        <Container className="flex h-[68px] items-center justify-between gap-4 lg:h-[76px]">
          <Link to="/" className="shrink-0" aria-label="KhozaLand home" data-testid="header-logo-link">
            <img src="/images/khozaland-logo-sm.png" alt="KhozaLand logo" width="356" height="259" className="h-11 w-auto lg:h-[52px]" />
          </Link>

          <nav aria-label="Main" className="hidden items-center gap-6 xl:flex" data-testid="desktop-nav">
            {NAV.map((item) =>
              item.children ? (
                <DesktopDropdown key={item.label} item={item} />
              ) : (
                <NavLink key={item.to} to={item.to} end className={({ isActive }) => cn("kl-nav-link", isActive && "is-active")} data-testid={`nav-${slug(item.label)}-link`}>
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={whatsappLink(contextForPath(pathname).msg)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("whatsapp_click", { label: "header" })}
              className="kl-icon-btn"
              aria-label="WhatsApp KhozaLand (opens in a new tab)"
              data-testid="header-whatsapp-button"
            >
              <WhatsAppIcon className="h-5 w-5" />
            </a>
            <QuoteButton label="Request a Quote" className="hidden h-11 px-5 sm:inline-flex" testId="header-request-quote-button" />
            <button type="button" className="kl-icon-btn xl:hidden" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} data-testid="mobile-menu-open-button">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </Container>
      </header>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-[88vw] max-w-sm flex-col gap-0 overflow-y-auto bg-white p-0 [&>button.absolute]:hidden" data-testid="mobile-nav-sheet">
          <div className="flex items-center justify-between border-b border-black/10 px-5 py-4">
            <img src="/images/khozaland-logo-sm.png" alt="KhozaLand" className="h-10 w-auto" width="356" height="259" />
            <SheetClose className="kl-icon-btn" aria-label="Close menu" data-testid="mobile-menu-close-button"><X className="h-5 w-5" /></SheetClose>
          </div>
          <SheetTitle className="sr-only">Site navigation</SheetTitle>
          <SheetDescription className="sr-only">Browse KhozaLand services and products</SheetDescription>
          <nav aria-label="Mobile" className="flex-1 px-5 py-3">
            <Accordion type="multiple" className="w-full">
              {NAV.map((item) =>
                item.children ? (
                  <AccordionItem key={item.label} value={item.label} className="border-black/10">
                    <AccordionTrigger className="py-4 text-base font-semibold hover:no-underline" data-testid={`mobile-nav-${slug(item.label)}-accordion`}>{item.label}</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-1 pb-2">
                        {item.children.map((c) => (
                          <li key={c.to}>
                            <Link to={c.to} className="block rounded-[10px] px-3 py-2.5 text-[15px] text-black/75 hover:bg-[#F7F7F7]" data-testid={`mobile-nav-${slug(c.label)}-link`}>{c.label}</Link>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ) : (
                  <Link key={item.to} to={item.to} className={cn("flex border-b border-black/10 py-4 text-base font-semibold", pathname === item.to && "underline underline-offset-8")} data-testid={`mobile-nav-${slug(item.label)}-link`}>
                    {item.label}
                  </Link>
                )
              )}
              <Link to="/faqs" className="flex border-b border-black/10 py-4 text-base font-semibold" data-testid="mobile-nav-faqs-link">FAQs</Link>
            </Accordion>
          </nav>
          <div className="space-y-2 border-t border-black/10 bg-[#F7F7F7] p-5">
            <QuoteButton label="Request a Quote" className="w-full" testId="mobile-nav-quote-button" />
            <WhatsAppButton className="w-full" testId="mobile-nav-whatsapp-button" />
            <p className="pt-2 text-center text-xs tracking-[0.22em] text-black/50">{SITE.motto}</p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
